import { Inject, Options, Vue } from "vue-property-decorator";
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import ModalBox from "@/shared/components/modal-box/index.vue";
import { EventEmitter } from "events";
import ErrorIndicator from "@/shared/components/error-indicator/index.vue";
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { TrustAPI } from "@/shared/modules/trust-api";
import { AccountingAppView, BreachView, ClosedInvestigationDetails, CompletedInvestigationDetails, InvestigationDetailsRequest, InvestigationTypeView, InvestigationView, InvestigatorWorkingPaperView, NoteView, ReportResult, TrustTeamMemberView } from "@/shared/modules/typescript-trustapi-client/models";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import { AppInsights } from "@/trust-admin-app/appInsights";
import store from "@/trust-admin-app/store";
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import AddeditIWPModal from "../add-edit-iwp-modal/index.vue";
import { FilePreviewData } from "@/shared/components/file-preview/file-preview-data";
import FilePreview from "@/shared/components/file-preview/index.vue";
import AlertBox from "@/shared/components/alert-box/index.vue";

@Options({
  props: {
    investigation: {},
  },
  components: {
    ModalBox,
    AlertBox,
    LoadingSpinner,
    LoadingIndicator,
    ErrorIndicator,
    DatePicker,
    AddeditIWPModal,
    FilePreview
  },
  mixins: [
    FormatDateTimeMixin,
  ]
})
export default class InvestigationDetailsModal extends Vue {
  private investigation!: InvestigationView;

  private ai = new AppInsights;
  private selectedFileForPreview: FilePreviewData = new FilePreviewData();
  private filePreviewState = 1;
  private showFilePreview = false;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  private closeModal(): void {
    this.$emit('closeModal');
  }

  private loadingContent = false;
  private errorLoadingDataSet = false;
  private loadingIWPList = false;
  private errorLoadingIWPList = false;
  private investigators: TrustTeamMemberView[] = [];
  private accountingApps: AccountingAppView = {};
  private reportResults: ReportResult[] = [];
  private breaches: BreachView[] = [];
  private investigationTypes: InvestigationTypeView[] = [];
  private investigationDetails: InvestigationView[] = [];
  private notes: NoteView[] = [];
  private status = 0;
  private commencementDate = new Date();
  private canSubmit = false;
  private workingPapers: InvestigatorWorkingPaperView[] = [];
  private showAddEditIWPModal = false;
  private selectedIWP: InvestigatorWorkingPaperView = null as any;
  private showFinalisationAlert = false;


  mounted(): void {
    this.preloadData();
    this.eventCtrl.on('load-iwp-list', this.loadIWPList);
    this.status = this.investigation.investigationStatus.id;
    this.commencementDate = new Date(this.investigation.commencementDate);
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('load-iwp-list', this.loadIWPList);
  }

  private async preloadData(): Promise<void> {
    try {
      // const _memberId: any = parseInt(this.$route.query.memberId as string, 10) || '';
      this.loadingContent = true;
      this.errorLoadingDataSet = false;
      const resp: any[] = await Promise.all([
        this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetTrustTeamMembersPost({}),
        this.apiService.ConfigTrustApi?.apiV2ConfigTrustGetInvestigationTypesPost(),
        this.apiService.CodeApi?.apiV2CodeGetReportResultsGet(),
        this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationAccountingAppsByIdInvestigationIdGet(this.investigation.id),
        this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationBreachesByIdInvestigationIdGet(this.investigation.id),
        this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationDetailsByIdInvestigationIdGet(this.investigation.id),
        this.apiService.NotesApi?.apiV2NotesGetNotesByInvestigationIdInvestigationIdGet(this.investigation.id),
        this.apiService.InvestigatorWorkingPaperApi?.apiV2InvestigatorWorkingPaperGetInvestigatorWorkingPapersByIdInvestigationIdGet(this.investigation.id),
      ]);
      this.investigators = resp[0].data.data;
      this.investigationTypes = resp[1].data.data;
      this.reportResults = resp[2].data.data;
      this.accountingApps = resp[3].data.data;
      this.breaches = resp[4].data.data;
      this.investigationDetails = resp[5].data.data;
      this.notes = resp[6].data.data;
      this.workingPapers = resp[7].data.data;
    } catch (err) {
      this.errorLoadingDataSet = true;
      this.loadingContent = false;
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load investigation data: ' + err);
      this.ai.trackException('Error, Unable to load investigation data: ' + err);
    } finally {
      this.loadingContent = false;
    }
  }

  private checkCanSubmit() {
    if (this.commencementDate) {
      this.canSubmit = true;
    }
  }

  private submitChanges() {
    if (this.canSubmit) {
      switch (this.status) {
        case 2:
          this.updateInvestigationDetails();
          break;
        case 3:
          if (!this.investigation.isReplyRequired) {
            this.showFinalisationAlert = true;
          } else {
            this.updateCompletedInvestigationDetails();
          }
          break;
        case 4:
          this.updateClosedInvestigationDetails();
          break;
      }
    }
    this.canSubmit = false;
  }

  private cancelFinalisation() {
    this.investigation.isReplyRequired = true;
    this.showFinalisationAlert = false;
  }

  private updateCompletedInvestigation() {
    this.showFinalisationAlert = false;
    this.updateCompletedInvestigationDetails(true);
  }

  private updateInvestigationDetails() {
    this.loadingContent = true;
    const _body: InvestigationDetailsRequest = {
      id: this.investigation.id,
      commencementDate: this.commencementDate,
      investigationTypeId: this.investigation.investigationType.id,
      trustTeamMemberId: this.investigation.trustTeamMember.id,
    }

    this.apiService.InvestigationApi?.apiV2InvestigationUpdateInvestigationDetailsPost(_body)
      .then(() => {
        this.loadingContent = false;
        this.eventCtrl.emit('reload-investigation-data');
        this.eventCtrl.emit('show-snackbar', 'success', 'Investigation details updated');
      })
      .catch(err => {
        this.loadingContent = false;
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to update investigation');
        this.ai.trackException('Error, Unable to update investigation: ' + err);
      })
  }

  private updateCompletedInvestigationDetails(closeAfterUpdate?: boolean) {
    const _body: CompletedInvestigationDetails = {
      id: this.investigation.id,
      reportResultId: this.investigation.reportResult.id,
    }
    this.apiService.InvestigationApi?.apiV2InvestigationUpdateCompletedInvestigationDetailsPost(_body)
      .then(() => {
        this.loadingContent = false;
        this.eventCtrl.emit('reload-investigation-data');
        this.eventCtrl.emit('show-snackbar', 'success', 'Investigation details updated');
        if (closeAfterUpdate) {
          this.closeModal();
        }
      })
      .catch(err => {
        this.loadingContent = false;
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to update investigation');
        this.ai.trackException('Error, Unable to update investigation: ' + err);
      })
  }

  private updateClosedInvestigationDetails() {
    const _body: ClosedInvestigationDetails = {
      id: this.investigation.id,
      reportResultId: this.investigation.reportResult.id,
    }
    this.apiService.InvestigationApi?.apiV2InvestigationUpdateClosedInvestigationDetailsPost(_body)
      .then(() => {
        this.loadingContent = false;
        this.eventCtrl.emit('reload-investigation-data');
        this.eventCtrl.emit('show-snackbar', 'success', 'Investigation details updated');
      })
      .catch(err => {
        this.loadingContent = false;
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to update investigation');
        this.ai.trackException('Error, Unable to update investigation: ' + err);
      })
  }

  private openAddEditIWPModal(iwp: InvestigatorWorkingPaperView): void {
    this.selectedIWP = iwp;
    this.selectedIWP.investigationId = this.investigation.id;
    this.showAddEditIWPModal = true;
  }

  private async loadIWPList(): Promise<void> {
    try {
      this.loadingIWPList = true;
      this.errorLoadingIWPList = false;
      const resp: any = await this.apiService.InvestigatorWorkingPaperApi?.apiV2InvestigatorWorkingPaperGetInvestigatorWorkingPapersByIdInvestigationIdGet(this.investigation.id);
      this.workingPapers = resp.data.data;
    } catch (error) {
      this.errorLoadingIWPList = true;
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to load IWP');
    } finally {
      this.loadingIWPList = false;
    }
  }

  private async getFileStream(file: InvestigatorWorkingPaperView): Promise<void> {

    try {
      this.filePreviewState = 1;
      const resp: any = await this.apiService.InvestigatorWorkingPaperApi?.apiV2InvestigatorWorkingPaperGetFileStreamByInvestigatorWorkingPaperIdInvestigatorWorkingPaperIdGet(
        file.id,
        { responseType: 'blob' }
      );
      this.selectedFileForPreview = new FilePreviewData(
        file.file.fileName,
        file.file.fileExtension.substring(1),
        resp.data,
        resp.headers['content-type']
      );
      this.openFilePreviewer(this.selectedFileForPreview);
      this.filePreviewState = 2;
    } catch (error) {
      this.filePreviewState = 3;
      console.log(error);
    }

  }

  private openFilePreviewer(file: FilePreviewData): void {
    this.selectedFileForPreview = file;
    this.showFilePreview = true;
  }

  private changeFilePreviewState(state: number) {
    this.filePreviewState = state;
  }

  private closeFilePreview(): void {
    this.showFilePreview = !this.showFilePreview;
    this.selectedFileForPreview = new FilePreviewData();
  }

}

